import React, { useState, useRef, useEffect, useMemo } from 'react';

import {

  motion,

  AnimatePresence,

  useScroll,

  useTransform,

  useMotionValueEvent,

} from 'motion/react';

import {

  X,

  Plus,

  Minus,

  Star,

  ShieldCheck,

  ThumbsUp,

  ShoppingBag,

  Check,

  Sparkles,

} from 'lucide-react';

import { toast } from '../ui/notify';

import { img_url } from '../../api/index';

import { QrProduct } from './ProductCard';

import { useBodyScrollLock } from './useBodyScrollLock';

import { useQrLanguage } from './LanguageContext';



interface ProductDetailModalProps {

  product: QrProduct | null;

  isOpen: boolean;

  onClose: () => void;

  onAddToCart: (product: QrProduct, quantity: number, detailId?: string | number | null) => void;

  currency?: string;

  allProducts?: QrProduct[];

}



const getImageUrl = (img?: string | null) => {

  if (!img) return '';

  return img.startsWith('http') ? img : `${img_url}/${img}`;

};



const formatPrice = (price: number | string) => {

  const n = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(n)) return '0';

  return n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

};



// Category type detection from name (works with API category names in any language)

type ProductType = 'main' | 'drink' | 'dessert' | 'side' | 'salad' | 'soup' | 'pizza' | 'burger' | 'pasta' | 'meat' | 'sushi' | 'breakfast' | 'unknown';



const detectProductType = (product: QrProduct): ProductType => {

  const name = `${product.categoryName || ''} ${product.name || ''}`.toLowerCase();

  if (/(içecek|drink|kahve|coffee|çay|tea|kola|cola|su\b|water|ayran|smoothie|juice|meşrubat|soda)/.test(name)) return 'drink';

  if (/(tatlı|dessert|baklava|sütlaç|cheesecake|pasta\s*tatlı|tiramisu|ice\s*cream|dondurma|pudding|kek\b|cake|profiterol|künefe)/.test(name)) return 'dessert';

  if (/(salata|salad)/.test(name)) return 'salad';

  if (/(çorba|soup)/.test(name)) return 'soup';

  if (/(pizza)/.test(name)) return 'pizza';

  if (/(burger|hamburger)/.test(name)) return 'burger';

  if (/(makarna|pasta|spaghetti|linguine|penne|noodle)/.test(name)) return 'pasta';

  if (/(et\b|meat|steak|kebap|kebab|köfte|pirzola|tavuk|chicken|kuzu|lamb|dana|beef|biftek|şiş)/.test(name)) return 'meat';

  if (/(sushi|maki|nigiri|sashimi)/.test(name)) return 'sushi';

  if (/(kahvaltı|breakfast|menemen|yumurta|egg|omlet)/.test(name)) return 'breakfast';

  if (/(patates|fries|side|garnitür|aperatif|nugget|soğan halkası|onion ring)/.test(name)) return 'side';

  return 'unknown';

};

// Light haptic feedback helper
const haptic = (ms = 40) => {
  if (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate) {
    window.navigator.vibrate(ms);
  }
};



// Smart pairing matrix - what goes well with what

const PAIRINGS: Record<ProductType, ProductType[]> = {

  main: ['drink', 'side', 'salad', 'dessert'],

  burger: ['side', 'drink', 'dessert', 'salad'],

  pizza: ['drink', 'salad', 'dessert', 'side'],

  pasta: ['drink', 'salad', 'dessert', 'soup'],

  meat: ['side', 'drink', 'salad', 'dessert'],

  sushi: ['drink', 'soup', 'salad', 'dessert'],

  salad: ['drink', 'soup', 'dessert', 'main'],

  soup: ['main', 'drink', 'salad', 'dessert'],

  breakfast: ['drink', 'dessert', 'side', 'salad'],

  drink: ['dessert', 'side', 'main', 'pizza'],

  dessert: ['drink', 'main', 'pizza', 'breakfast'],

  side: ['drink', 'main', 'burger', 'pizza'],

  unknown: ['drink', 'dessert', 'side', 'main'],

};



/**

 * AI-style smart algorithm for "Often bought with" recommendations.

 * Scores each candidate product on:

 *   - Pairing affinity (matrix-driven)

 *   - Rating bonus

 *   - Featured/popular bonus

 *   - Price compatibility (similar or smaller price)

 *   - Diversity (avoid same category)

 */

const computeOftenBoughtWith = (

  current: QrProduct,

  all: QrProduct[],

  maxResults = 4,

): QrProduct[] => {

  if (!all || all.length === 0) return [];

  const currentType = detectProductType(current);

  const pairTypes = PAIRINGS[currentType] || PAIRINGS.unknown;

  const currentPrice = Number(current.price) || 0;



  type Candidate = { product: QrProduct; score: number };

  const scored: Candidate[] = [];



  for (const p of all) {

    if (String(p.id) === String(current.id)) continue;

    if (!p.name) continue;



    const pType = detectProductType(p);



    // Pairing score (matrix index = priority)

    const pairIdx = pairTypes.indexOf(pType);

    const pairScore = pairIdx >= 0 ? 100 - pairIdx * 15 : 10;



    // Rating bonus

    const ratingScore = ((p.rating || 4.5) - 4) * 12;



    // Popular / featured bonus

    const popScore = (p.isFeatured ? 18 : 0) + (p.isPopular ? 14 : 0) + (p.isNew ? 8 : 0);



    // Price compatibility (prefer items <= current price + 50%)

    const price = Number(p.price) || 0;

    let priceScore = 0;

    if (price > 0 && currentPrice > 0) {

      const ratio = price / currentPrice;

      if (ratio <= 0.5) priceScore = 8;

      else if (ratio <= 1) priceScore = 16;

      else if (ratio <= 1.5) priceScore = 10;

      else priceScore = 0;

    }



    // Diversity bonus (different category from current)

    const diversityScore =

      String(p.category) !== String(current.category) ? 14 : -10;



    const total = pairScore + ratingScore + popScore + priceScore + diversityScore;

    scored.push({ product: p, score: total });

  }



  scored.sort((a, b) => b.score - a.score);

  // Keep diversity: avoid 2 of the same type

  const seenTypes = new Set<ProductType>();

  const result: QrProduct[] = [];

  for (const c of scored) {

    const t = detectProductType(c.product);

    if (seenTypes.has(t) && seenTypes.size >= 2) continue;

    seenTypes.add(t);

    result.push(c.product);

    if (result.length >= maxResults) break;

  }

  return result;

};



// Mock variant data for non-API extras. Labels are pulled from translations

// at render-time below, so the IDs / price modifiers can stay constant here.

const MOCK_MEATS: Array<{ id: 'chicken' | 'beef' | 'lamb'; priceModifier: number }> = [

  { id: 'chicken', priceModifier: 0 },

  { id: 'beef', priceModifier: 30 },

  { id: 'lamb', priceModifier: 60 },

];



const MOCK_EXTRAS: Array<{ id: 'cheese' | 'avocado' | 'bacon' | 'mushroom'; price: number }> = [

  { id: 'cheese', price: 25 },

  { id: 'avocado', price: 35 },

  { id: 'bacon', price: 45 },

  { id: 'mushroom', price: 20 },

];



export default function ProductDetailModal({

  product,

  isOpen,

  onClose,

  onAddToCart,

  currency = '₼',

  allProducts = [],

}: ProductDetailModalProps) {

  const { t } = useQrLanguage();

  // Translation lookup tables for the mock variant labels.

  const meatLabels: Record<'chicken' | 'beef' | 'lamb', string> = {

    chicken: t.meatChicken,

    beef: t.meatBeef,

    lamb: t.meatLamb,

  };

  const extraLabels: Record<'cheese' | 'avocado' | 'bacon' | 'mushroom', string> = {

    cheese: t.extraCheese,

    avocado: t.extraAvocado,

    bacon: t.extraBacon,

    mushroom: t.extraMushroom,

  };

  const [quantity, setQuantity] = useState(1);

  const [selectedDetail, setSelectedDetail] = useState<any>(null);

  const [selectedMeat, setSelectedMeat] = useState<string>('');

  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);

  const [imgError, setImgError] = useState(false);

  const [showOftenBought, setShowOftenBought] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);



  /* ------------------------------------------------------------------

   * Wolt/Uber-Eats style scroll-driven hero animation

   * NOTE: We animate transform / filter / opacity (compositor-only) instead

   * of `height` so it never causes layout reflow on mobile.  The image stays

   * in the natural scroll flow so users actually see it on open.

   * ------------------------------------------------------------------ */

  const { scrollY } = useScroll({ container: scrollRef });

  // Image: subtle parallax + zoom + blur + fade

  const imageScale = useTransform(scrollY, [-150, 0, 280], [1.35, 1.05, 1.18]);

  const imageY = useTransform(scrollY, [0, 280], [0, 90]);

  const imageBlurValue = useTransform(scrollY, [0, 120, 280], [0, 6, 18]);

  const imageFilter = useTransform(imageBlurValue, (b) => `blur(${b}px)`);

  const imageOpacity = useTransform(scrollY, [200, 320], [1, 0.25]);

  // Sticky top bar: bg + title fade in

  const stickyBgOpacity = useTransform(scrollY, [80, 180], [0, 1]);

  const stickyTitleOpacity = useTransform(scrollY, [120, 200], [0, 1]);



  useBodyScrollLock(isOpen);



  // Track scroll to reveal "Often bought with" — small threshold so it appears

  // as soon as user starts scrolling past the hero image.

  useMotionValueEvent(scrollY, 'change', (latest) => {

    if (latest > 60 && !showOftenBought) setShowOftenBought(true);

  });



  useEffect(() => {

    if (!isOpen) {

      setQuantity(1);

      setSelectedDetail(null);

      setSelectedMeat('');

      setSelectedExtras([]);

      setImgError(false);

      setShowOftenBought(false);

      // Reset scroll to top when closing

      if (scrollRef.current) scrollRef.current.scrollTop = 0;

    }

  }, [isOpen]);



  // Detect product type to know which mock variants to show

  const productType = useMemo(

    () => (product ? detectProductType(product) : 'unknown'),

    [product],

  );



  // Often-bought-with recommendations

  const recommendations = useMemo(

    () => (product ? computeOftenBoughtWith(product, allProducts, 4) : []),

    [product, allProducts],

  );



  if (!product) return null;



  const basePrice = typeof product.price === 'string' ? parseFloat(product.price) : product.price;

  const detailPrice = selectedDetail ? Number(selectedDetail.price) : basePrice;

  const meatModifier = selectedMeat

    ? MOCK_MEATS.find((m) => m.id === selectedMeat)?.priceModifier || 0

    : 0;

  const extrasTotal = selectedExtras.reduce(

    (acc, id) => acc + (MOCK_EXTRAS.find((e) => e.id === id)?.price || 0),

    0,

  );

  const activePrice = detailPrice + meatModifier + extrasTotal;

  const totalPrice = activePrice * quantity;

  const hasDetails = Array.isArray(product.details) && product.details.length > 0;

  const imgUrl = getImageUrl(product.image);

  const rating = typeof product.rating === 'number' ? product.rating : 4.7;



  const showMeat = ['burger', 'meat', 'pasta'].includes(productType);

  const showExtras = ['burger', 'pizza', 'salad', 'pasta'].includes(productType);



  const toggleExtra = (id: string) => {

    setSelectedExtras((prev) =>

      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],

    );

  };



  const handleAddToCart = () => {
    haptic(15);
    onAddToCart(product, quantity, selectedDetail ? selectedDetail.id : null);

    toast.success(t.itemsAddedToCart(quantity, product.name), { position: 'top-center' });

    onClose();

  };



  const handleAddRecommendation = (p: QrProduct, e: React.MouseEvent) => {
    haptic(10);
    e.stopPropagation();

    onAddToCart(p, 1);

    toast.success(t.itemAddedToCart(p.name), { position: 'top-center' });

  };



  return (

    <AnimatePresence>

      {isOpen && (

        <motion.div

          initial={{ opacity: 0, y: 50 }}

          animate={{ opacity: 1, y: 0 }}

          exit={{ opacity: 0, y: 50 }}

          transition={{ type: 'spring', bounce: 0.1, duration: 0.5 }}

          className="fixed inset-0 z-[80] bg-[#F8F9FA] dark:bg-[#09090b] qrm-no-select overflow-hidden"

        >

          {/* ===== Sticky frosted top bar (fades in on scroll, Wolt-style) ===== */}

          <div className="fixed top-0 left-0 right-0 z-40 qrm-safe-top pointer-events-none">

            <motion.div

              className="absolute inset-0 overflow-hidden"

              style={{ opacity: stickyBgOpacity }}

            >

              {imgUrl && !imgError ? (

                <img

                  src={imgUrl}

                  alt=""

                  aria-hidden="true"

                  className="absolute inset-0 w-full h-full object-cover scale-150"

                />

              ) : null}

              <div className="absolute inset-0 backdrop-blur-2xl bg-white/80 dark:bg-[#09090b]/80 border-b border-gray-100/60 dark:border-white/10" />

            </motion.div>

            <div className="relative flex items-center gap-3 px-4 py-3">

              <motion.h2

                style={{ opacity: stickyTitleOpacity }}

                className="font-extrabold text-gray-900 dark:text-white text-base truncate flex-1"

              >

                {product.name}

              </motion.h2>

              <button

                type="button"

                onClick={onClose}

                className="w-10 h-10 bg-black/40 dark:bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/60 active:scale-90 transition-all shrink-0 pointer-events-auto"

                aria-label="close"

              >

                <X className="w-5 h-5" />

              </button>

            </div>

          </div>



          {/* ===== Main scroll container (image + cards) ===== */}

          <div

            ref={scrollRef}

            className="absolute inset-0 qrm-modal-scroll scrollbar-hide overflow-y-auto overscroll-contain"

          >

            {/* Hero image — natural flow, takes ~45vh */}

            <div className="relative w-full h-[45vh] overflow-hidden bg-gray-200 dark:bg-[#1a1a1a]">

              {imgUrl && !imgError ? (

                <motion.img

                  src={imgUrl}

                  alt={product.name}

                  onError={() => setImgError(true)}

                  className="absolute inset-0 w-full h-full object-cover"

                  style={{

                    scale: imageScale,

                    y: imageY,

                    opacity: imageOpacity,

                    filter: imageFilter,

                  }}

                />

              ) : (

                <motion.div

                  className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-amber-500"

                  style={{

                    scale: imageScale,

                    y: imageY,

                    opacity: imageOpacity,

                  }}

                >

                  <span className="text-7xl">🍽️</span>

                </motion.div>

              )}

              {/* Top vignette so close button is readable */}

              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />

              {/* Bottom fade so card edge blends in */}

              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#F8F9FA] dark:from-[#09090b] to-transparent pointer-events-none" />

            </div>



            {/* ===== Content — ONE consolidated card with side margins (matches Wolt/UberEats style) ===== */}

            <div className="relative -mt-8 z-20 px-4 pb-44">

              {/* MAIN CARD — wraps title, price, description, info-cards, AND all variants */}

              <div className="bg-white dark:bg-[#1a1a1a] rounded-[28px] p-5 shadow-[0_-8px_30px_rgb(0,0,0,0.08)] dark:shadow-[0_-8px_30px_rgb(0,0,0,0.4)] border border-gray-100 dark:border-white/5">

                {/* Drag handle indicator */}

                <div className="w-12 h-1.5 bg-gray-200 dark:bg-white/15 rounded-full mx-auto -mt-1 mb-4" />



                {/* Title + rating row */}

                <div className="flex justify-between items-start gap-4 mb-2">

                  <h1 className="text-[22px] font-extrabold text-gray-900 dark:text-white tracking-tight leading-snug">

                    {product.name}

                  </h1>

                  <div className="flex items-center gap-1 text-[13px] font-bold bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-500/15 dark:to-amber-500/15 px-2.5 py-1 rounded-lg shrink-0 text-gray-900 dark:text-white border border-orange-100 dark:border-orange-500/20">

                    <Star className="w-3.5 h-3.5 text-orange-500 fill-orange-500" />

                    <span>{rating.toFixed(1)}</span>

                  </div>

                </div>



                {/* Price */}

                <span className="text-[26px] font-black text-orange-500 tracking-tight block mb-3">

                  {currency}

                  {formatPrice(activePrice)}

                </span>



                {/* Description */}

                {product.description ? (

                  <p className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed font-medium mb-5">

                    {product.description}

                  </p>

                ) : (

                  <div className="mb-2" />

                )}



                {/* === Nested info-cards (Şefin Özel + Kaliteli İçerik) === */}

                <div className="flex flex-col gap-3 mb-5">

                  <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-orange-50 dark:bg-orange-500/10 border border-orange-100 dark:border-orange-500/20">

                    <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center shrink-0">

                      <ThumbsUp className="w-5 h-5 text-orange-600 dark:text-orange-400" />

                    </div>

                    <div className="min-w-0">

                      <h4 className="text-[14px] font-bold text-gray-900 dark:text-white">

                        {t.chefSpecial}

                      </h4>

                      <p className="text-[12px] text-gray-500 dark:text-gray-400 font-medium">

                        {t.chefSpecialDesc}

                      </p>

                    </div>

                  </div>



                  <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5">

                    <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-white/10 flex items-center justify-center shrink-0">

                      <ShieldCheck className="w-5 h-5 text-gray-600 dark:text-gray-300" />

                    </div>

                    <div className="min-w-0">

                      <h4 className="text-[14px] font-bold text-gray-900 dark:text-white">

                        {t.qualityIngredients}

                      </h4>

                      <p className="text-[12px] text-gray-500 dark:text-gray-400 font-medium">

                        {t.qualityIngredientsDesc}

                      </p>

                    </div>

                  </div>

                </div>



                {/* === Size / variants from API (NESTED inside main card) === */}

                {hasDetails && (

                  <div className="mb-5">

                    <h3 className="text-[13px] font-black text-gray-900 dark:text-white mb-3 uppercase tracking-wider">

                      {t.selectSize}

                    </h3>

                    <div className={`grid gap-3 ${product.details!.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>

                      {product.details!.map((detail: any) => {

                        const isSelected = selectedDetail?.id === detail.id;

                        const detailPriceNum = Number(detail.price);

                        const diff = detailPriceNum - basePrice;

                        return (

                          <button

                            key={detail.id}

                            type="button"

                            onClick={() =>

                              setSelectedDetail(

                                isSelected

                                  ? null

                                  : {

                                    id: detail.id,

                                    name: `${detail.count || ''} ${detail.unit || ''}`.trim(),

                                    price: detailPriceNum,

                                  },

                              )

                            }

                            className={`relative p-4 rounded-2xl border-2 transition-all active:scale-95 ${isSelected

                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 shadow-lg shadow-orange-500/20'

                              : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-orange-300'

                              }`}

                          >

                            {isSelected && (

                              <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">

                                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />

                              </div>

                            )}

                            <div className="text-center">

                              <p

                                className={`text-sm font-bold mb-1 ${isSelected

                                  ? 'text-orange-600 dark:text-orange-400'

                                  : 'text-gray-900 dark:text-white'

                                  }`}

                              >

                                {detail.count} {detail.unit || ''}

                              </p>

                              <p

                                className={`text-xs font-bold ${isSelected

                                  ? 'text-orange-500'

                                  : 'text-gray-500 dark:text-gray-400'

                                  }`}

                              >

                                {currency}

                                {formatPrice(detailPriceNum)}

                                {!isNaN(diff) && diff !== 0 && (

                                  <span className="ml-1 text-[10px] opacity-70">

                                    ({diff > 0 ? '+' : ''}

                                    {formatPrice(diff)})

                                  </span>

                                )}

                              </p>

                            </div>

                          </button>

                        );

                      })}

                    </div>

                  </div>

                )}



                {/* === Mock meat-type selection (NESTED inside main card) === */}

                {showMeat && (

                  <div className="mb-5">

                    <h3 className="text-[13px] font-black text-gray-900 dark:text-white mb-3 uppercase tracking-wider">

                      {t.meatType}

                    </h3>

                    <div className="grid grid-cols-3 gap-3">

                      {MOCK_MEATS.map((meat) => {

                        const isSelected = selectedMeat === meat.id;

                        return (

                          <button

                            key={meat.id}

                            type="button"

                            onClick={() =>

                              setSelectedMeat(isSelected ? '' : meat.id)

                            }

                            className={`relative p-4 rounded-2xl border-2 transition-all active:scale-95 ${isSelected

                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 shadow-lg shadow-orange-500/20'

                              : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-orange-300'

                              }`}

                          >

                            {isSelected && (

                              <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">

                                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />

                              </div>

                            )}

                            <div className="text-center">

                              <p

                                className={`text-sm font-bold mb-1 ${isSelected

                                  ? 'text-orange-600 dark:text-orange-400'

                                  : 'text-gray-900 dark:text-white'

                                  }`}

                              >

                                {meatLabels[meat.id]}

                              </p>

                              {meat.priceModifier !== 0 ? (

                                <p

                                  className={`text-xs font-bold ${isSelected

                                    ? 'text-orange-500'

                                    : 'text-gray-500 dark:text-gray-400'

                                    }`}

                                >

                                  +{currency}

                                  {meat.priceModifier}

                                </p>

                              ) : (

                                <p

                                  className={`text-xs font-bold ${isSelected

                                    ? 'text-orange-500'

                                    : 'text-gray-500 dark:text-gray-400'

                                    }`}

                                >

                                  {t.standard}

                                </p>

                              )}

                            </div>

                          </button>

                        );

                      })}

                    </div>

                  </div>

                )}



                {/* === Mock extras (NESTED inside main card) === */}

                {showExtras && (

                  <div>

                    <h3 className="text-[13px] font-black text-gray-900 dark:text-white mb-3 uppercase tracking-wider">

                      {t.extras}

                    </h3>

                    <div className="grid grid-cols-2 gap-3">

                      {MOCK_EXTRAS.map((extra) => {

                        const isSelected = selectedExtras.includes(extra.id);

                        return (

                          <button

                            key={extra.id}

                            type="button"

                            onClick={() => toggleExtra(extra.id)}

                            className={`relative p-4 rounded-2xl border-2 transition-all text-left active:scale-95 ${isSelected

                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 shadow-lg shadow-orange-500/20'

                              : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-orange-300'

                              }`}

                          >

                            {isSelected && (

                              <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">

                                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />

                              </div>

                            )}

                            <p

                              className={`text-sm font-bold mb-1 ${isSelected

                                ? 'text-orange-600 dark:text-orange-400'

                                : 'text-gray-900 dark:text-white'

                                }`}

                            >

                              {extraLabels[extra.id]}

                            </p>

                            <p

                              className={`text-xs font-bold ${isSelected

                                ? 'text-orange-500'

                                : 'text-gray-500 dark:text-gray-400'

                                }`}

                            >

                              +{currency}

                              {extra.price}

                            </p>

                          </button>

                        );

                      })}

                    </div>

                  </div>

                )}

              </div>

              {/* End of MAIN CARD */}



              {/* === Often Bought With — appears ONLY when user scrolls down === */}

              <AnimatePresence>

                {showOftenBought && recommendations.length > 0 && (

                  <motion.div

                    key="often-bought-with"

                    initial={{ opacity: 0, y: 24 }}

                    animate={{ opacity: 1, y: 0 }}

                    exit={{ opacity: 0, y: 24 }}

                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}

                    className="mt-6"

                  >

                    <div className="flex items-center gap-2 mb-3 px-1">

                      <Sparkles className="w-5 h-5 text-orange-500" />

                      <h3 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">

                        {t.oftenBoughtTogether}

                      </h3>

                    </div>

                    <div className="grid grid-cols-2 gap-3">

                      {recommendations.map((rec) => {

                        const recImg = getImageUrl(rec.image);

                        return (

                          <div

                            key={`rec-${rec.id}`}

                            className="bg-white dark:bg-[#1a1a1a] rounded-2xl overflow-hidden border border-gray-100 dark:border-white/5 shadow-sm relative"

                          >

                            {(rec.isPopular || rec.isFeatured) && (

                              <div className="absolute top-2.5 left-2.5 z-10 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md">

                                <Star className="w-2.5 h-2.5 fill-current" />

                                {t.popular}

                              </div>

                            )}

                            <div className="aspect-square w-full bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/20">

                              {recImg ? (

                                <img

                                  src={recImg}

                                  alt={rec.name}

                                  className="w-full h-full object-cover"

                                />

                              ) : (

                                <div className="w-full h-full flex items-center justify-center text-3xl">

                                  🍽️

                                </div>

                              )}

                            </div>

                            <div className="p-3">

                              <div className="flex items-start justify-between gap-2">

                                <div className="flex-1 min-w-0">

                                  <span className="text-orange-500 dark:text-orange-400 text-[14px] font-black block leading-none mb-1">

                                    {currency}

                                    {formatPrice(rec.price)}

                                  </span>

                                  <h4 className="text-[12px] font-bold text-gray-900 dark:text-white line-clamp-2 leading-tight">

                                    {rec.name}

                                  </h4>

                                </div>

                                <button

                                  type="button"

                                  onClick={(e) => handleAddRecommendation(rec, e)}

                                  className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shrink-0 hover:bg-orange-600 active:scale-90 transition-all shadow-md"

                                  aria-label="add"

                                >

                                  <Plus className="w-5 h-5" />

                                </button>

                              </div>

                            </div>

                          </div>

                        );

                      })}

                    </div>

                  </motion.div>

                )}

              </AnimatePresence>

            </div>

          </div>



          {/* Sticky Bottom Bar */}

          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-2xl border-t border-gray-100 dark:border-white/10 z-50 qrm-safe-bottom">

            <div className="flex items-center gap-3 max-w-md mx-auto">

              <div className="flex items-center bg-gray-100 dark:bg-white/10 rounded-2xl p-1 shrink-0">

                <button

                  type="button"

                  onClick={() => { haptic(8); setQuantity(Math.max(1, quantity - 1)); }}

                  className="w-11 h-11 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-white/10 rounded-xl transition-colors disabled:opacity-50 active:scale-90"

                  disabled={quantity <= 1}

                >

                  <Minus className="w-5 h-5" />

                </button>

                <span className="w-9 text-center font-bold text-base text-gray-900 dark:text-white">

                  {quantity}

                </span>

                <button

                  type="button"

                  onClick={() => { haptic(8); setQuantity(quantity + 1); }}

                  className="w-11 h-11 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-white/10 rounded-xl transition-colors active:scale-90"

                >

                  <Plus className="w-5 h-5" />

                </button>

              </div>



              <button

                type="button"

                onClick={handleAddToCart}

                className="flex-1 h-[52px] rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-[14px] transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 active:scale-95"

              >

                <ShoppingBag className="w-5 h-5" />

                {t.addToCart} • {currency}

                {formatPrice(totalPrice)}

              </button>

            </div>

          </div>

        </motion.div>

      )}

    </AnimatePresence>

  );

}